/**
 * Implements a uniswap datafeed
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  ResolutionString,
  ErrorCallback,
  HistoryCallback,
  LibrarySymbolInfo,
  PeriodParams,
  SubscribeBarsCallback,
  IDatafeedChartApi,
  SearchSymbolsCallback,
} from '../charting_library';
import {
  CONV_SYMBOL,
  filterSymbolFromConv,
  pairConvertable,
} from './helpers/symbols';
import { KattanaAPI, subscription } from '../../../store/createStore';
import eventEmitter from '../../../core/eventEmitter';
import { STABLE_CURRENCIES } from '../../../helpers/stable-currencies';
import { getLiveTradesEventName } from '../../../helpers/events-builder';
import { enums, helpers, ITicker, IDexChartTrade } from '@kattana-io/common';
import normalizeCandles from './helpers/normalizeCandles';
import useTicker from '../../../hooks/useTicker';
import { enableLiquiditFilter } from '../../Modal/TickersModal/helper';
const { checkAddressChecksum } = require('ethereum-checksum-address');

dayjs.extend(utc);
dayjs.extend(timezone);

interface IDexSymbol {
  symbol: string; //WETH/USDT
  symbol_name: string; // WETHUSDT
  base_asset: string; // WETH
  quote_asset: string; // USDT
  base_address: string;
  quote_address: string;
  precision?: any;
}

interface IFindSymbolPayload {
  symbolId?: number;
  query?: string;
  address?: string;
}

export interface ICandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

const supported_resolutions = [
  '1S',
  '5S',
  '10S',
  '1',
  '3',
  '5',
  '15',
  '30',
  '60',
  '120',
  '240',
  '360',
  '480',
  '720',
  '1D',
  '1W',
  '1M',
];

/**
 * Route name used in backend
 */

interface IUniswapConstructorOptions {
  exchange: string;
  debug: boolean;
}
interface IChartSymbol {
  symbol: string;
  symbol_name?: string;
  base_asset: string;
  quote_asset: string;
  base_address: string;
  quote_address: string;
  address?: string;
  original_quote: string;
  precision: number;
  flipped: boolean;
  ticker: ITicker;
}
/* eslint-disable no-restricted-syntax */
class DexDatafeed implements IDatafeedChartApi {
  debug: boolean;
  symbols: IDexSymbol[];
  exchangeName: string;
  subscriptions: any;
  lastBar: any;
  firstBar: any;
  onTick: any;
  timeframe: enums.ETimeframes | undefined;
  usdConversion: boolean;
  buttonUSD: any;
  eventName: string;
  ticker: ITicker | null;
  onResetCacheNeededCallback: any;
  onLastPriceHandler: any;

  constructor(options: IUniswapConstructorOptions) {
    this.exchangeName =
      options.exchange.charAt(0).toLocaleUpperCase() +
      options.exchange.slice(1); // make the first letter capital ("sushiswap" -> "Sushiswap")
    this.symbols = [];
    this.debug = options.debug || false;
    this.usdConversion = true;
    this.eventName = '';
    this.ticker = null;
    this.lastBar = {};
    this.firstBar = {};

    this.subscriptions = {};
  }

  /**
   * Set flag and refetch candles
   */
  toggleUSDConversion = () => {
    this.usdConversion = !this.usdConversion;
  };

  /**
   * Request remote symbols
   */
  private findSymbols = async ({
    symbolId,
    query,
    address,
  }: IFindSymbolPayload): Promise<IChartSymbol[]> => {
    const tickers = await KattanaAPI.tickers({
      id: symbolId,
      searchRaw: query,
      sort: { name: 'liquidity', order: 'DESC' },
      address,
    });
    if (!tickers) {
      return [];
    }

    return tickers.map((item: ITicker) => {
      const {
        base,
        quote,
        name,
        base_address,
        base_decimals,
        quote_address,
        quote_decimals,
      } = useTicker(item);

      return {
        symbol: name,
        symbol_name: item.address, // well, this is how we get input
        base_asset: base,
        quote_asset: quote,
        original_quote: quote,
        base_address: base_address,
        quote_address: quote_address,
        address: item.address,
        precision: item.flipped ? quote_decimals : base_decimals,
        flipped: item.flipped,
        ticker: item,
      };
    });
  };

  fetchKlines = async (
    symbol: LibrarySymbolInfo & any,
    interval: number | string,
    startTime: number,
    endTime: number,
    limit: number = 500,
    poolCreated: number
  ): Promise<IDexChartTrade[]> => {
    try {
      const timeframe = helpers.datafeedCommom.mapInterval(String(interval));
      this.timeframe = timeframe;

      const convertation = this.usdConversion
        ? symbol.data.original_quote
        : undefined;

      let liquidityFilter = 0;

      if (enableLiquiditFilter(this.ticker?.chain || '')) {
        liquidityFilter = 1;
        const liquidity = this.ticker?.liquidity;

        if (liquidity && liquidity > 0) {
          if (liquidity > 10_000_000) {
            liquidityFilter = 2;
          }
          if (liquidity > 100_000_000) {
            liquidityFilter = 3;
          }
        }
      }

      const data = await KattanaAPI.chart(
        symbol.chain,
        timeframe,
        symbol.data.address,
        startTime,
        endTime,
        limit,
        convertation,
        symbol.data.flipped,
        symbol.data.precision,
        poolCreated,
        liquidityFilter
      );

      if (!data) {
        return [];
      }

      return data as IDexChartTrade[];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  public isIntervalSupported = (interval: string): boolean =>
    supported_resolutions.includes(interval);

  public onReady = async (callback: any) => {
    try {
      callback({
        supports_marks: false,
        supports_timescale_marks: false,
        supports_time: true,
        supported_resolutions,
      });
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * TV method: when user types in search field
   * @param userInput
   * @param exchange
   * @param symbolType
   * @param onResultReadyCallback
   */
  public searchSymbols = async (
    userInput: string,
    exchange: string,
    symbolType: string,
    onResultReadyCallback: SearchSymbolsCallback
  ) => {
    const symbolId = -1;
    await this.findSymbols({ symbolId, query: userInput }).then(
      (symbols: any[]) => {
        onResultReadyCallback(
          symbols.map((symbol) => ({
            symbol: symbol.symbol,
            full_name: symbol.symbol,
            description: `${symbol.base_asset} / ${symbol.quote_asset}`,
            pair: `${symbol.quoteAsset}-${symbol.base_asset}`,
            ticker: symbol.symbol,
            exchange: this.exchangeName,
            chain: symbol.chain,
            type: 'stock',
          }))
        );
      }
    );
  };

  /**
   * Pass button
   * @param button
   */
  setButton = (button: any) => {
    this.buttonUSD = button;
  };

  private precision(a: number) {
    if (!isFinite(a)) {
      return 0;
    }
    let e = 1,
      p = 0;
    while (Math.round(a * e) / e !== a && p < 28) {
      e *= 10;
      p++;
    }
    return Math.round(p / 2);
  }

  /**
   * Creates a fake USD converted symbol if necessary
   * @param symbol
   * @param USDConverted
   * @returns
   */
  private symbolIterator = (symbol: IChartSymbol) => {
    // const GENERIC_PRICESCALE = 10 ** 8; // USD precision
    const convertableAddress = symbol.ticker.flipped
      ? symbol.ticker.base_address
      : symbol.ticker.quote_address;

    if (pairConvertable(symbol.ticker.chain, convertableAddress)) {
      if (this.buttonUSD) {
        this.buttonUSD.style.display = 'block';
      }
      if (this.usdConversion) {
        let price = symbol.ticker.price_a_usd;
        if (symbol.ticker.flipped) {
          price = symbol.ticker.price_b_usd;
        }
        const pricePrecision = this.precision(price);
        return {
          name: symbol.symbol,
          description: `${symbol.base_asset} / USD (converted)`,
          pair: symbol.symbol,
          ticker: symbol.address,
          session: '24x7',
          minmov: 1,
          pricescale: 10 ** pricePrecision,
          timezone: 'UTC',
          has_seconds: true,
          has_intraday: true,
          has_daily: true,
          has_weekly_and_monthly: true,
          original_currency_code: symbol.quote_asset,
          currency_code: 'USD',
          data: symbol,
          exchange: symbol.ticker.exchange,
          precision: symbol.precision,
          chain: symbol.ticker.chain,
        };
      }
    } else {
      this.usdConversion = false;
      if (this.buttonUSD) {
        this.buttonUSD.style.display = 'none';
      }
    }

    let price = symbol.ticker.price_a;
    if (symbol.ticker.flipped) {
      price = symbol.ticker.price_b;
    }
    const pricePrecision = this.precision(price);
    return {
      name: symbol.symbol,
      description: `${symbol.base_asset} / ${symbol.quote_asset}`,
      pair: `${symbol.base_asset}/${symbol.quote_asset}`,
      ticker: symbol.address,
      session: '24x7',
      minmov: 1,
      pricescale: 10 ** pricePrecision,
      timezone: 'UTC',
      has_seconds: true,
      has_intraday: true,
      has_daily: true,
      has_weekly_and_monthly: true,
      original_currency_code: symbol.quote_asset,
      currency_code: symbol.quote_asset,
      data: symbol,
      exchange: symbol.ticker.exchange,
      precision: symbol.precision,
      chain: symbol.ticker.chain,
    };
  };

  /**
   * TV method
   * Find symbol
   * @param symbol_name
   * @param onSymbolResolvedCallback
   * @param onResolveErrorCallback
   */
  public resolveSymbol(
    symbol_name: string,
    onSymbolResolvedCallback: any,
    onResolveErrorCallback: any
  ) {
    if (this.debug) {
      console.log('👉 resolveSymbol:', symbol_name);
    }
    // move "{id}:" to constant
    const searchById = symbol_name.includes('{id}:');
    let validName = '';
    let symbolId;

    const comps = symbol_name.split(':');
    if (!searchById) {
      // eslint-disable-next-line no-param-reassign
      symbol_name = (comps.length > 1 ? comps[1] : symbol_name).trim();

      const shouldConvert = symbol_name.includes(CONV_SYMBOL);
      validName = shouldConvert
        ? filterSymbolFromConv(symbol_name)
        : symbol_name;

      symbolId = -1;
    } else {
      symbolId = Number(comps[1]);
    }

    const isValidAddress = checkAddressChecksum(validName);
    const isPolars = validName.includes('-');
    const payload: IFindSymbolPayload = {};
    if (symbolId) {
      payload.symbolId = symbolId;
    }
    if (isValidAddress || isPolars) {
      payload.address = validName;
    } else {
      payload.query = validName;
    }

    this.findSymbols(payload).then((symbols: IChartSymbol[]) => {
      const [symbol] = symbols.map((symbol) => this.symbolIterator(symbol));
      if (symbol) {
        this.ticker = symbol.data.ticker;
        onSymbolResolvedCallback(symbol);
      } else {
        onResolveErrorCallback('Not found');
      }
    });
  }

  /**
   * Convert Binance's kline to candle
   * @param {*} kline
   */
  // Bitquery
  // klineToCandle = (kline: any) => ({
  //     time: kline[0],
  //     open: kline[1],
  //     high: kline[2],
  //     low: kline[3],
  //     close: kline[4],
  //     volume: kline[5]
  // })

  // Kattana trades adapter
  klineToCandle = (kline: IDexChartTrade): ICandle => ({
    time: Number(kline.date * 1000),
    open: parseFloat(kline.open.toString()),
    high: parseFloat(kline.high.toString()),
    low: parseFloat(kline.low.toString()),
    close: parseFloat(kline.close.toString()),
    volume: this.usdConversion
      ? parseFloat(kline.volume) * parseFloat(kline.close.toString())
      : parseFloat(kline.volume),
  });

  getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onResult: HistoryCallback,
    onError: ErrorCallback
  ) {
    if (this.debug) {
      console.log('👉 getBars:', symbolInfo.name, resolution);
      console.log('First:', periodParams.firstDataRequest);
      console.log(
        'From:',
        periodParams.from,
        new Date(periodParams.from).toUTCString()
      );
      console.log(
        'To:  ',
        periodParams.to,
        new Date(periodParams.to).toUTCString()
      );
    }

    if (!resolution) {
      onError('Invalid interval');
    }

    let totalKlines: IDexChartTrade[] = [];

    const finishKlines = () => {
      if (this.debug) {
        console.log('📊:', totalKlines.length);
      }

      if (this.ticker) {
        if (totalKlines.length === 0) {
          if (periodParams.from > this.ticker.pool_created * 1000) {
            onResult([]);
          } else {
            onResult([], { noData: true });
          }
        } else {
          // Get last candle from first request (right to left)
          // Otherwise we will take invalid candle
          if (periodParams.firstDataRequest) {
            const lastBar = this.klineToCandle(
              totalKlines[totalKlines.length - 1]
            );
            this.lastBar[this.ticker.address] = lastBar;
          } else {
            // if it's not first request - we need to glue our parts of chart
            if (this.firstBar.close) {
              totalKlines[totalKlines.length - 1].close = this.firstBar.close;
            }
          }

          this.firstBar = totalKlines[0];
          onResult(normalizeCandles(totalKlines.map(this.klineToCandle)), {
            noData: false,
          });
        }
      }
    };

    // eslint-disable-next-line no-shadow
    const getKlines = (
      from: number,
      to: number,
      poolCreated: number,
      limit = 500
    ) => {
      this.fetchKlines(symbolInfo, resolution, from, to, limit, poolCreated)
        .then((klines) => {
          totalKlines = totalKlines.concat(klines);
          finishKlines();
          return null;
        })
        .catch((err) => {
          console.error(err);
          onError('Some problem');
        });
    };

    // // eslint-disable-next-line no-param-reassign
    // periodParams.from *= 1000;
    // // eslint-disable-next-line no-param-reassign
    // periodParams.to *= 1000;
    const limit = periodParams.countBack ? periodParams.countBack : 500;

    getKlines(
      periodParams.from,
      periodParams.to,
      this.ticker?.pool_created || 0,
      limit
    );
  }

  /**
   * Access ticker object if any
   * @returns
   */
  public getTicker = () => {
    return this.ticker;
  };

  getSubscriptionName = (symInfo: any, subscriberUID: string) => {
    const [baseAsset, quoteAsset] = this.ticker
      ? this.ticker.name.split('/')
      : symInfo.name.split('/');
    const chain = this.ticker ? this.ticker.chain : symInfo.data.ticker.chain;

    const subscription = getLiveTradesEventName(chain, baseAsset, quoteAsset);

    this.subscriptions[subscriberUID] = subscription;
    return subscription;
  };

  onLastPrice = (message: any) => {
    const { trade } = message;

    let price, priceUSD;
    if (this.ticker) {
      if (this.ticker.flipped) {
        price = trade.priceb;
        priceUSD = trade.priceb_usd;
      } else {
        price = trade.pricea;
        priceUSD = trade.pricea_usd;
      }
    }

    if (trade && this.ticker && this.timeframe) {
      if (this.usdConversion && !priceUSD) {
        return;
      }
      let lastPrice = price;

      let quote;
      if (this.ticker.flipped) {
        [quote] = this.ticker.name.split('/');
      } else {
        [, quote] = this.ticker.name.split('/');
      }
      if (STABLE_CURRENCIES.includes(quote) || this.usdConversion) {
        lastPrice = priceUSD;
      }

      const newTime = helpers.datafeedCommom.formatDateByTimeframe(
        trade.timestamp,
        this.timeframe
      );

      if (this.lastBar[this.ticker.address]) {
        const decimals = this.ticker.base_decimals;

        let newLastBar;
        if (newTime > this.lastBar[this.ticker.address].time) {
          let amount = trade.amount0;
          if (this.ticker.flipped) {
            amount = trade.amount1;
          }

          // new candle
          newLastBar = {
            close: lastPrice,
            high: lastPrice,
            low: lastPrice,
            open: this.lastBar[this.ticker.address].close,
            volume: this.usdConversion
              ? (amount / 10 ** decimals) * lastPrice
              : amount / 10 ** decimals,
          };
        } else {
          let amount = trade.amount0;
          if (this.ticker.flipped) {
            amount = trade.amount1;
          }

          let volume;
          const currentVolume = this.lastBar[this.ticker.address].volume;

          if (this.usdConversion) {
            volume = currentVolume + (amount / 10 ** decimals) * lastPrice;
          } else {
            volume = currentVolume + amount / 10 ** decimals;
          }

          // update last candle
          newLastBar = {
            ...this.lastBar[this.ticker.address],
            high: Math.max(this.lastBar[this.ticker.address].high, lastPrice),
            low: Math.min(this.lastBar[this.ticker.address].low, lastPrice),
            close: lastPrice,
            volume,
          };
        }
        newLastBar.time = newTime;
        if (newTime >= this.lastBar[this.ticker.address].time) {
          this.onTick({ ...newLastBar });
          this.lastBar[this.ticker.address] = newLastBar;
        }
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  subscribeBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    onTick: SubscribeBarsCallback,
    listenerGuid: string,
    onResetCacheNeededCallback: () => void
  ) {
    this.onResetCacheNeededCallback = onResetCacheNeededCallback;
    if (this.debug) {
      console.log('👉 listenerGuid:', listenerGuid);
    }
    this.onTick = onTick;
    this.eventName = this.getSubscriptionName(symbolInfo, listenerGuid);

    /**
     * Live event for trades
     */
    if (this.eventName) {
      eventEmitter.addListener(this.eventName, this.onLastPrice);
    }
    subscription.subscribeToLiveTrades(
      this.eventName,
      (symbolInfo as any).data.ticker
    );
  }

  unsubscribeBars(subscriberUID: any) {
    if (this.debug) {
      console.log('👉 unsubscribeBars:', subscriberUID);
    }
    if (this.eventName) {
      subscription.unsubscribeFromLiveTrades(this.eventName);
      eventEmitter.removeListener(this.eventName, this.onLastPrice);
    }
  }
}

export default DexDatafeed;
